/**
 * 按钮。刻意只做少量变体，保持视觉一致；不引重量级 UI 库。
 * 儿童模式用 size="kid"：点击区 ≥56px、字号 ≥18px（见方案 2.5）。
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'kid' | 'kid-green';
export type ButtonSize = 'sm' | 'md' | 'lg' | 'kid' | 'icon';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** 加载中：禁用并显示转圈，防止重复提交。 */
  loading?: boolean;
  fullWidth?: boolean;
  icon?: ReactNode;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 disabled:bg-brand-300',
  secondary:
    'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50 active:bg-slate-100 disabled:text-slate-400',
  ghost: 'bg-transparent text-brand-700 hover:bg-brand-50 active:bg-brand-100',
  danger: 'bg-rose-600 text-white hover:bg-rose-700 active:bg-rose-800 disabled:bg-rose-300',
  kid: 'bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700 shadow-md disabled:bg-brand-300',
  'kid-green':
    'bg-emerald-500 text-white hover:bg-emerald-600 active:bg-emerald-700 shadow-md disabled:bg-emerald-300',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm rounded-lg',
  md: 'h-11 px-4 text-base rounded-xl',
  lg: 'h-12 px-6 text-base rounded-xl',
  // 56px 下限，文字 20px（text-xl），图标+文字布局
  kid: 'min-h-[56px] px-6 py-3 text-xl rounded-2xl gap-3',
  /**
   * **只给"纯图标"按钮用**（图标自己带 `text-base leading-none` 的容器）。
   *
   * 2026-10-14 用户实测："这些控件能不能是同一样式的？每个都是不一样的样式，很难看。"
   * 根因是图标按钮借用了 `md`（`h-11 px-4`）：高度虽然都是 44px，**宽度却跟着字形走**
   * （实测同一行里 42 / 43.19 / 53.97 / 55.97px 四种宽度），再加上 ↑↓ 用 `ghost`、
   * 🗑 用 `danger`（没有边框），一行里就出现了四五种外观。
   *
   * 这里单独立一个**固定 44×44 的正方形**尺寸：`h-11 w-11` + `p-0` 的意思是不再由
   * 文字撑宽度，`shrink-0` 保证窄屏被挤压时也不变形（既有约定：按钮列不参与收缩）。
   * ⚠️ 这是**新增**的 size，默认仍是 `md`，所以其它页面零影响。
   */
  icon: 'h-11 w-11 p-0 text-base rounded-xl shrink-0',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  icon,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex select-none items-center justify-center font-semibold transition-colors',
        'disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      ) : (
        icon
      )}
      {children}
    </button>
  );
}
